import { useEffect, useState } from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { SignIn, User, Lock, Buildings, Phone, IdentificationCard, UserCircle, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Spinner from "@/components/Spinner";
import { authApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage({ mode }) {
  const { user, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    nombre_empresa: "",
    nombre_responsable: "",
    telefono: "",
    ruc: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Reset success screen si cambia el modo (login <-> register)
  useEffect(() => {
    setSuccessMessage(null);
  }, [mode]);

  if (user) return <Navigate to={location.state?.from || "/"} replace />;

  const isLogin = mode === "login";
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && (!acceptTerms || !acceptPrivacy)) {
      return toast.error("Debe aceptar los Términos y la Política de Privacidad para continuar");
    }
    setBusy(true);
    try {
      if (isLogin) {
        await login(form.email, form.password, remember);
        toast.success("Sesión iniciada");
      } else {
        const r = await authApi.register({
          email: form.email,
          password: form.password,
          nombre_empresa: form.nombre_empresa,
          nombre_responsable: form.nombre_responsable,
          telefono: form.telefono,
          ruc: form.ruc,
          acepta_terminos: acceptTerms && acceptPrivacy,
        });
        setSuccessMessage(r.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo completar la operación");
    } finally {
      setBusy(false);
    }
  };

  // Pantalla de confirmación post-registro.
  if (successMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-lg bg-card border border-border rounded-md p-8 text-center" data-testid="register-success-screen">
          <div className="w-16 h-16 rounded-md bg-success/10 grid place-items-center mx-auto mb-5 text-success">
            <CheckCircle size={30} weight="duotone" />
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">¡Registro recibido!</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed whitespace-pre-line">
            {successMessage}
          </p>
          <div className="mt-6">
            <Button
              variant="outline"
              data-testid="back-to-login-btn"
              onClick={() => {
                setSuccessMessage(null);
                navigate("/login");
              }}
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:flex lg:w-1/2 bg-primary text-primary-foreground p-12 flex-col justify-between">
        <Link to="/landing" className="flex items-center gap-3 hover:opacity-90 transition-opacity" data-testid="auth-brand-link">
          <div
            className="w-10 h-10 rounded-md bg-primary-foreground text-primary grid place-items-center font-bold text-2xl leading-none"
            style={{ fontFamily: '"Times New Roman", Times, serif' }}
          >R</div>
          <div>
            <div className="font-heading font-semibold text-2xl tracking-tight leading-none">Recobro</div>
            <div className="text-xs opacity-70 uppercase tracking-widest mt-1">Cartera al día</div>
          </div>
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-semibold tracking-tight leading-tight max-w-md">
            Automatiza la recuperación de tu cartera vencida.
          </h2>
          <p className="mt-5 opacity-80 max-w-sm leading-relaxed">
            Clasificación IA · Mensajes profesionales · Convenios de pago · Validación OCR de comprobantes · Reportes en tiempo real.
          </p>
        </div>
        <div className="text-xs opacity-60">© 2026 Recobro</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <form onSubmit={submit} className="w-full max-w-md space-y-4 py-8" data-testid={isLogin ? "login-form" : "register-form"}>
          <div className="mb-4">
            <div className="overline">{isLogin ? "Acceso" : "Nueva cuenta"}</div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight mt-1">
              {isLogin ? "Bienvenido de vuelta" : "Solicite su cuenta Recobro"}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {isLogin
                ? "Ingrese sus credenciales para entrar."
                : "Su cuenta queda en verificación hasta que el equipo de Recobro la active (máx. 24 horas)."}
            </p>
          </div>

          {!isLogin && (
            <>
              <div>
                <Label htmlFor="empresa">Nombre del negocio <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="empresa" value={form.nombre_empresa} onChange={(e) => set("nombre_empresa", e.target.value)} required data-testid="input-empresa" className="pl-9" placeholder="Distribuidora Andina S.A." />
                </div>
              </div>

              <div>
                <Label htmlFor="responsable">Nombre del responsable <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <UserCircle size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="responsable" value={form.nombre_responsable} onChange={(e) => set("nombre_responsable", e.target.value)} required data-testid="input-responsable" className="pl-9" placeholder="Juan Pérez" />
                </div>
              </div>

              <div>
                <Label htmlFor="telefono">Teléfono / WhatsApp <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} required minLength={7} data-testid="input-telefono" className="pl-9" placeholder="0991234567" />
                </div>
              </div>

              <div>
                <Label htmlFor="ruc">RUC o cédula (opcional)</Label>
                <div className="relative">
                  <IdentificationCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="ruc" value={form.ruc} onChange={(e) => set("ruc", e.target.value)} data-testid="input-ruc" className="pl-9" placeholder="1791234567001" />
                </div>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="email">Correo <span className="text-destructive">*</span></Label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required autoComplete="email" data-testid="input-email" className="pl-9" />
            </div>
          </div>

          <div>
            <Label htmlFor="password">Contraseña <span className="text-destructive">*</span></Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="password" type={showPassword ? "text" : "password"} minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} required autoComplete={isLogin ? "current-password" : "new-password"} data-testid="input-password" className="pl-9 pr-10" />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                data-testid="toggle-password-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                {showPassword ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
              </button>
            </div>
          </div>

          {isLogin && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                data-testid="remember-me-checkbox"
              />
              <Label htmlFor="remember" className="text-sm font-normal cursor-pointer select-none">
                Recordarme en este dispositivo
              </Label>
            </div>
          )}

          {!isLogin && (
            <div className="space-y-2 p-3 rounded-md border border-border bg-accent/20">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={(v) => setAcceptTerms(!!v)}
                  data-testid="accept-terms-checkbox"
                  className="mt-0.5"
                />
                <Label htmlFor="terms" className="text-sm font-normal leading-snug cursor-pointer">
                  He leído y acepto los{" "}
                  <a
                    href="/legal/terminos-y-condiciones.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-medium hover:underline"
                    data-testid="terms-link"
                  >
                    Términos y Condiciones (PDF)
                  </a>
                  .
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="privacy"
                  checked={acceptPrivacy}
                  onCheckedChange={(v) => setAcceptPrivacy(!!v)}
                  data-testid="accept-privacy-checkbox"
                  className="mt-0.5"
                />
                <Label htmlFor="privacy" className="text-sm font-normal leading-snug cursor-pointer">
                  He leído y acepto la{" "}
                  <a
                    href="/legal/politica-de-privacidad.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-medium hover:underline"
                    data-testid="privacy-link"
                  >
                    Política de Privacidad (PDF)
                  </a>
                  .
                </Label>
              </div>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={busy || (!isLogin && (!acceptTerms || !acceptPrivacy))} data-testid="submit-auth-btn">
            {busy ? <Spinner size="s" className="mr-2" /> : <SignIn size={16} weight="bold" className="mr-2" />}
            {busy ? "Procesando…" : isLogin ? "Iniciar sesión" : "Solicitar cuenta"}
          </Button>

          <div className="text-sm text-muted-foreground text-center">
            {isLogin ? (
              <>¿Primera vez? <Link to="/register" className="text-primary font-medium hover:underline" data-testid="link-register">Solicite acceso</Link></>
            ) : (
              <>¿Ya tiene cuenta? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">Inicie sesión</Link></>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
